import { useState } from 'react'
import { sanitizeInput } from '../../utils/sanitizeInput'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { Mail, Lock, Eye, EyeOff } from 'lucide-react'

export default function Login() {
  const navigate = useNavigate()
  const { signIn, signInWithGoogle } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: sanitizeInput(e.target.value),
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setErrorMsg('')

    const result = await signIn(formData.email, formData.password)
    if (result.success) {
      navigate('/dashboard')
    } else if (result.error === 'Email o contraseña incorrectos') {
      setErrorMsg('Revisa tu nombre de usuario o contraseña')
    } else if (result.error) {
      setErrorMsg(result.error)
    }
    setIsLoading(false)
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    setErrorMsg('')
    
    const result = await signInWithGoogle()
    if (result.success) {
      navigate('/dashboard')
    } else if (result.error && result.error !== 'Inicio de sesión cancelado') {
      setErrorMsg(result.error)
    }
    setIsLoading(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] to-[#0F3D2E] flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-[#222222] p-8 rounded-2xl shadow-2xl border border-[#0F3D2E]">
        {/* Header */}
        <div>
          <div className="flex justify-center mb-6">
            <img 
              src="/kiniela_logo.png" 
              alt="Kiniela.mx Logo" 
              className="h-40 w-auto object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-center text-[#D4AF37]">
            Iniciar Sesión
          </h2>
          <p className="mt-2 text-center text-sm text-[#BDBDBD]">
            Accede a tu cuenta de Kiniela.mx
          </p>
        </div>


        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {errorMsg && (
            <div className="text-[#B11217] text-center font-medium mb-2">{errorMsg}</div>
          )}
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8]" size={20} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10 input bg-[#1A1A1A] text-[#F8F8F8] border-[#0F3D2E] focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#F8F8F8] hover:text-[#D4AF37]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>


          {/* Forgot Password & Home Link */}
          <div className="flex items-center justify-between">
            <Link to="/" className="text-sm text-[#F8F8F8] hover:text-[#D4AF37]">
              ← Volver al inicio
            </Link>
            <Link to="/forgot-password" className="text-sm text-[#D4AF37] hover:text-[#C19F2E]">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12] py-3 text-lg"
          >
            {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#0F3D2E]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#222222] text-[#BDBDBD]">o</span>
          </div>
        </div>

        {/* Google Sign In Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-[#0F3D2E] rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-[#1A1A1A] font-medium">Continuar con Google</span>
        </button>

        {/* Register Link */}
        <p className="text-center text-sm text-[#F8F8F8]">
          ¿No tienes cuenta?{' '}
          <Link to="/register" className="font-medium text-[#D4AF37] hover:text-[#C19F2E]">
            Regístrate aquí
          </Link>
        </p>
      </div>
    </div>
  )
}
